import { useQuery } from '@tanstack/react-query';
import {
  FilterTag,
  FilterDropdown,
  ComboboxSearch,
} from 'modules/shared-components/filters';
import { useMemo } from 'react';
import {
  MarketplaceService,
  marketplaceKeys,
} from 'api/services/httpServices/MarketplaceService';
import { useDebouncedState } from 'utils/useDebounce';
import { MizarHexagonImage } from 'modules/shared-components/hex-logo';

type TraderTagProps = { value: string } & Omit<
  React.ComponentProps<typeof FilterTag>,
  'label'
>;

export function TraderFilterTag({ value: id, ...rest }: TraderTagProps) {
  const { data: trader } = useQuery({
    queryFn: () => MarketplaceService.getTrader(id),
    queryKey: marketplaceKeys.getTrader(id),
  });

  return trader ? <FilterTag {...rest} label={trader.name} /> : null;
}

type TraderFilterProps = {
  value: string[];
  onChange: (value: string[]) => void;
};

export function TraderFilter({ value = [], onChange }: TraderFilterProps) {
  const [query, debouncedQuery, setQuery] = useDebouncedState('', 250);
  const {
    data: traders,
    isFetching,
    isError,
  } = useQuery({
    queryFn: () => MarketplaceService.getTraders({ name: debouncedQuery }),
    queryKey: marketplaceKeys.getTraders({ name: debouncedQuery }),
    enabled: debouncedQuery.length >= 2,
    keepPreviousData: true,
  });

  const options = useMemo(() => {
    return traders?.map((trader) => ({
      label: trader.name,
      value: trader.id,
      icon: (
        <MizarHexagonImage size={24} alt="Trader logo" src={trader.imgUrl} />
      ),
    }));
  }, [traders]);

  return (
    <FilterDropdown
      value={value}
      options={options}
      label="Trader"
      onChange={(val) => {
        onChange(val);
        setQuery('');
      }}
      isError={isError}
      search={
        <ComboboxSearch
          query={query}
          setQuery={setQuery}
          isLoading={isFetching}
        />
      }
    />
  );
}
